import React, { useMemo, useState } from 'react';
import { getFareSnapshot, getRideDistanceKm, getRideDurationMin, formatMoney } from '../utils/rideData';
import { fareRows, fareRowLabel } from '../utils/fareRows';

const ChevronDownIcon = ({ open }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: open ? 'rotate(90deg)' : 'none',
      transition: 'transform 0.2s ease',
    }}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

function TripFareDetails({ ride, t, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const pickupName = ride?.pickup?.name || (ride?.pickup?.address ? ride.pickup.address.split(',')[0] : t('book.pickup'));
  const pickupAddr = ride?.pickup?.address || t('book.pickup');

  const dropName = ride?.destination?.name || (ride?.destination?.address ? ride.destination.address.split(',')[0] : t('book.drop'));
  const dropAddr = ride?.destination?.address || t('book.drop');

  const fareSnapshot = getFareSnapshot(ride);
  const fare = fareSnapshot?.total ?? ride?.fare ?? null;
  const distanceKm = getRideDistanceKm(ride);
  const durationMin = getRideDurationMin(ride);

  const parts = useMemo(() => fareSnapshot, [fareSnapshot]);

  return (
    <div className="active-route-fare-card">
      <div className="route-fare-main-row">
        {/* Route Timeline */}
        <div className="active-route-timeline">
          <div className="active-timeline-track" aria-hidden>
            <span className="active-dot pickup" />
            <span className="active-track-line" />
            <span className="active-dot drop" />
          </div>
          <div className="active-timeline-labels">
            <div className="active-point-block">
              <span className="active-point-name">{pickupName}</span>
              <span className="active-point-addr">{pickupAddr}</span>
            </div>
            <div className="active-point-block">
              <span className="active-point-name">{dropName}</span>
              <span className="active-point-addr">{dropAddr}</span>
            </div>
          </div>
        </div>

        {/* Fare & ETA summary */}
        <div className="active-fare-summary-col">
          <div className="active-fare-amount">{fare != null ? formatMoney(fare) : '—'}</div>
          <span className="active-cash-badge">{t('pay.cash')}</span>
          <div className="active-distance-time">
            {distanceKm != null ? `${distanceKm.toFixed(1)} km` : t('details.unavailable')} • {durationMin != null ? `${durationMin} min` : t('details.unavailable')}
          </div>
        </div>
      </div>

      {/* Expandable Trip details trigger */}
      <button
        type="button"
        className="active-details-trigger-row"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>{t('active.tripDetails')}</span>
        <ChevronDownIcon open={open} />
      </button>

      {open && (
        <div className="active-details-expanded-body">
          {fareRows(parts).rows.map((row) => (
            <div className="fare-breakdown-row" key={row.id}>
              <span className="fare-label">{fareRowLabel(row, t, { distance: 'fare.distanceRate' })}</span>
              <span className="fare-val">{formatMoney(row.amount)}</span>
            </div>
          ))}
          <div className="fare-breakdown-row total">
            <span className="fare-label">{t('fare.totalEstimated')}</span>
            <span className="fare-val">{fare != null ? formatMoney(fare) : '—'}</span>
          </div>
          {!parts && <p className="fare-notice">{t('details.fareBreakdownUnavailable')}</p>}
          <p className="fare-notice">{t('fare.payCashNotice')}</p>
        </div>
      )}
    </div>
  );
}

export default React.memo(TripFareDetails, (prev, next) => (
  prev.t === next.t
  && prev.defaultOpen === next.defaultOpen
  && prev.ride?.fare === next.ride?.fare
  && prev.ride?.fareSnapshot?.waitingCharge === next.ride?.fareSnapshot?.waitingCharge
  && prev.ride?.distanceKm === next.ride?.distanceKm
  && prev.ride?.duration === next.ride?.duration
  && prev.ride?.pickup?.name === next.ride?.pickup?.name
  && prev.ride?.pickup?.address === next.ride?.pickup?.address
  && prev.ride?.destination?.name === next.ride?.destination?.name
  && prev.ride?.destination?.address === next.ride?.destination?.address
));
